import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

/**
 * YouTube API Compliance Tooltip
 *
 * Displays a disclaimer for metrics that are calculated by Streamers Connected
 * and not directly provided by YouTube's API, as required by YouTube API Policy III.E.4h
 */
const YouTubeEstimateTooltip = ({ metric }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const getMessage = () => {
    switch (metric) {
      case 'engagement':
        return 'Engagement Rate is calculated by Streamers Connected and is not provided by YouTube. Formula: (Likes + Comments) / Views × 100. This is an estimate and may differ from official YouTube Analytics.';
      case 'watchHours':
        return 'Watch Hours is calculated by Streamers Connected and is not provided by YouTube. This is an estimate based on average CCV and stream duration, and may differ from official YouTube Analytics.';
      case 'views':
        return 'YouTube view counts are provided by the YouTube API and must be refreshed at least every 30 days to remain compliant. Data shown reflects the most recent API fetch and may not reflect real-time counts.';
      case 'impressions':
        return 'Impressions is calculated by Streamers Connected and is not provided by YouTube. This is an estimate and may differ from official YouTube Analytics.';
      default:
        return 'This metric is calculated by Streamers Connected and is not provided by YouTube. These are estimates and may differ from official YouTube Analytics.';
    }
  };

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, [isVisible]);

  return (
    <div className="inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        <HelpCircle className="w-3.5 h-3.5 text-white hover:text-white transition-colors" />
      </div>

      {isVisible && createPortal(
        <div
          className="fixed z-[99999] w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl border border-white pointer-events-none"
          style={{
            top: pos.top,
            left: pos.left,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <p className="leading-relaxed">
            {getMessage()}
          </p>
          <p className="mt-2 text-white text-[10px]">
            YouTube API Policy Compliance
          </p>
          <div className="absolute w-2 h-2 bg-gray-900 border-r border-b border-white/10 transform rotate-45 -bottom-1 left-1/2 -translate-x-1/2"></div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default YouTubeEstimateTooltip;
